import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Plus, Trash, X } from "@phosphor-icons/react";
import { api } from "../../utils/api";
import { comprimirImagen } from "../../utils/imagenes";
import { Button } from "../shared/Button";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Modal } from "../shared/Modal";
import { Input, Textarea, Select } from "../shared/Input";

// Categorías fijas — coinciden con los ambientes que ya se usan en el resto
// de la app (Cocinas, Suelos, etc.) para que el cliente reconozca el mismo
// lenguaje. "Otros" cubre cualquier trabajo realizado que no encaje en un
// ambiente puntual (ej. tabiquería, pintura general).
const CATEGORIAS = [
  { id: "salon", label: "Salón" },
  { id: "dormitorio", label: "Dormitorio" },
  { id: "cocina", label: "Cocina" },
  { id: "bano", label: "Baño" },
  { id: "terraza", label: "Terraza" },
  { id: "otros", label: "Otros" },
];
const labelCategoria = (id) => CATEGORIAS.find((c) => c.id === id)?.label || "Otros";

export const Catalogo = ({ isAdmin, adminToken }) => {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAdd, setModalAdd] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [nuevaFoto, setNuevaFoto] = useState({ titulo: "", descripcion: "", categoria: "salon", imagen: null, imagenPreview: null });
  const [categoriaActiva, setCategoriaActiva] = useState("todos");
  const [fotoAbierta, setFotoAbierta] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    cargarCatalogo();
  }, []);

  const cargarCatalogo = async () => {
    setError(false);
    try {
      const data = await api.get("/catalogo");
      setFotos(data);
    } catch (e) {
      console.error("Error cargando catálogo:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImagenChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const comprimida = await comprimirImagen(file);
      setNuevaFoto((prev) => ({ ...prev, imagen: comprimida, imagenPreview: comprimida }));
    } catch (err) {
      console.error("Error comprimiendo imagen:", err);
      alert("No se pudo procesar la foto — probá con otra.");
    }
  };

  const guardarFoto = async () => {
    if (!nuevaFoto.titulo || !nuevaFoto.imagen) return;
    setSubiendo(true);
    try {
      await api.post("/catalogo", nuevaFoto, adminToken);
      setModalAdd(false);
      setNuevaFoto({ titulo: "", descripcion: "", categoria: "salon", imagen: null, imagenPreview: null });
      cargarCatalogo();
    } catch (e) {
      console.error("Error guardando foto:", e);
      alert("No se pudo guardar la foto — probá de nuevo.");
    } finally {
      setSubiendo(false);
    }
  };

  const eliminarFoto = async (id) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/catalogo/${id}`, adminToken);
      setFotoAbierta(null);
      cargarCatalogo();
    } catch (e) {
      console.error("Error eliminando foto:", e);
    }
  };

  const fotosPorCategoria = useMemo(() => {
    if (categoriaActiva === "todos") return fotos;
    return fotos.filter((f) => (f.categoria || "otros") === categoriaActiva);
  }, [fotos, categoriaActiva]);

  // Solo mostramos como chips las categorías que realmente tienen al menos
  // una foto — no tiene sentido un filtro "Terraza" vacío si todavía no hay
  // nada cargado ahí.
  const categoriasConFotos = useMemo(() => {
    const presentes = new Set(fotos.map((f) => f.categoria || "otros"));
    return CATEGORIAS.filter((c) => presentes.has(c.id));
  }, [fotos]);

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="hero-header" style={{ padding: "24px 20px" }}>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="flex items-center gap-2 mb-2">
            <Camera size={28} weight="fill" style={{ color: "var(--brand-accent)" }} />
          </div>
          <div className="text-h2 font-display">Ambientes</div>
          <div className="text-small" style={{ opacity: 0.8, marginTop: 4 }}>
            Inspiración por espacio — trabajos realizados y estilos posibles
          </div>
        </div>
      </div>

      <div className="container" style={{ marginTop: -20, position: "relative", zIndex: 2 }}>
        {isAdmin && (
          <Button
            variant="accent"
            className="btn-full mb-4"
            onClick={() => setModalAdd(true)}
            icon={<Plus size={20} />}
          >
            Añadir foto al catálogo
          </Button>
        )}

        {categoriasConFotos.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto" style={{ paddingBottom: 2 }}>
            <button
              onClick={() => setCategoriaActiva("todos")}
              className={`chip ${categoriaActiva === "todos" ? "is-active" : ""}`}
              style={{
                flexShrink: 0, border: "1.5px solid var(--border-subtle)", borderRadius: 999, padding: "8px 14px",
                fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                background: categoriaActiva === "todos" ? "var(--brand-primary)" : "var(--surface-secondary)",
                color: categoriaActiva === "todos" ? "#fff" : "var(--text-primary)",
              }}
            >
              Todos
            </button>
            {categoriasConFotos.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategoriaActiva(c.id)}
                style={{
                  flexShrink: 0, border: "1.5px solid var(--border-subtle)", borderRadius: 999, padding: "8px 14px",
                  fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                  background: categoriaActiva === c.id ? "var(--brand-primary)" : "var(--surface-secondary)",
                  color: categoriaActiva === c.id ? "#fff" : "var(--text-primary)",
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {fotosPorCategoria.length === 0 ? (
          error ? (
            <EmptyState
              icon="⚠️"
              title="No se pudo cargar el catálogo"
              description="Revisá tu conexión e intentá de nuevo"
              action={<Button variant="outline" onClick={cargarCatalogo}>Reintentar</Button>}
            />
          ) : (
            <EmptyState
              icon="📷"
              title="Sin fotos aún"
              description="Próximamente añadiremos trabajos realizados"
            />
          )
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {fotosPorCategoria.map((foto, i) => (
              <motion.div
                key={foto.id || i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 10) * 0.04 }}
                className="card p-0 overflow-hidden"
                style={{ cursor: "pointer" }}
                onClick={() => setFotoAbierta(foto)}
              >
                <img
                  src={foto.imagen}
                  alt={foto.titulo}
                  loading="lazy"
                  style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }}
                />
                <div className="p-3">
                  <div className="font-bold text-tiny">{foto.titulo}</div>
                  <div className="text-tiny text-secondary">{labelCategoria(foto.categoria)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Ficha de detalle — foto grande + descripción, al tocar una tarjeta */}
      <AnimatePresence>
        {fotoAbierta && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setFotoAbierta(null)}
            style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "flex-end" }}
          >
            <motion.div
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              onClick={(e) => e.stopPropagation()}
              style={{ background: "var(--bg-base)", width: "100%", maxHeight: "92vh", overflow: "auto", borderRadius: "20px 20px 0 0" }}
            >
              <div style={{ position: "relative" }}>
                <img
                  src={fotoAbierta.imagen}
                  alt={fotoAbierta.titulo}
                  style={{ width: "100%", maxHeight: "55vh", objectFit: "cover", display: "block" }}
                />
                <button
                  onClick={() => setFotoAbierta(null)}
                  className="icon-button"
                  style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "50%" }}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4">
                <div className="text-tiny font-bold" style={{ color: "var(--brand-accent)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {labelCategoria(fotoAbierta.categoria)}
                </div>
                <div className="text-h3 font-display mt-1">{fotoAbierta.titulo}</div>
                {fotoAbierta.descripcion && (
                  <div className="text-small text-secondary mt-2">{fotoAbierta.descripcion}</div>
                )}
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-4"
                    onClick={() => eliminarFoto(fotoAbierta.id)}
                    icon={<Trash size={16} />}
                  >
                    Eliminar
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal añadir foto (solo admin) */}
      <Modal isOpen={modalAdd} onClose={() => setModalAdd(false)} title="Añadir Foto">
        <div>
          <Input
            label="Título del trabajo"
            placeholder="Ej: Reforma baño completo"
            value={nuevaFoto.titulo}
            onChange={(e) => setNuevaFoto((prev) => ({ ...prev, titulo: e.target.value }))}
          />
          <label className="input-label mt-3" style={{ display: "block" }}>Ambiente</label>
          <Select
            value={nuevaFoto.categoria}
            onChange={(e) => setNuevaFoto((prev) => ({ ...prev, categoria: e.target.value }))}
            options={CATEGORIAS.map((c) => ({ value: c.id, label: c.label }))}
          />
          <Textarea
            label="Descripción (opcional)"
            placeholder="Detalles del trabajo realizado"
            value={nuevaFoto.descripcion}
            onChange={(e) => setNuevaFoto((prev) => ({ ...prev, descripcion: e.target.value }))}
            rows={3}
          />
          <div className="input-group">
            <label className="input-label">Foto</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImagenChange}
              style={{ display: "none" }}
              id="file-catalogo"
            />
            {!nuevaFoto.imagenPreview ? (
              <Button
                variant="outline"
                className="btn-full"
                onClick={() => document.getElementById("file-catalogo").click()}
                icon={<Camera size={20} />}
              >
                Seleccionar foto
              </Button>
            ) : (
              <div>
                <img src={nuevaFoto.imagenPreview} alt="Preview" style={{ width: "100%", borderRadius: 8, marginBottom: 8 }} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNuevaFoto((prev) => ({ ...prev, imagen: null, imagenPreview: null }))}
                >
                  Cambiar foto
                </Button>
              </div>
            )}
          </div>
          <Button
            variant="accent"
            className="btn-full mt-4"
            onClick={guardarFoto}
            disabled={!nuevaFoto.titulo || !nuevaFoto.imagen || subiendo}
          >
            {subiendo ? "Guardando..." : "Guardar foto"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
